/**
 * DHTML Tools - Layer positioning and event registry utilities.
 * Provides cross-browser layer positioning and custom event handling.
 */

// Declare external dependencies

// Position constants
const _DHTMLTOOLS_JS_ = true;
const _TOP = 0;
const _LEFT = 0;
const _CENTER = 1;
const _MIDDLE = 1;
const _BOTTOM = 2;
const _RIGHT = 2;

let _whereAmI = "";

// Resize event registry
type ResizeCallback = (() => void) | string;
const resizeEvents: ResizeCallback[] = [];

/**
 * Executes all callbacks registered to occur on resize
 */
function doDHTMLResize(e?: Event): void {
  for (const event of resizeEvents) {
    if (typeof event === "function") {
      event();
    } else {
      throw new Error("registerResizeEvent no longer accepts strings. Pass a callback function instead.");
    }
  }
}

/**
 * Register a callback to be executed on resize
 * @param callback - Function to execute on window resize
 */
function registerResizeEvent(callback: () => void): void {
  resizeEvents.push(callback);
}

// Set up resize handler
if (typeof window !== "undefined") {
  window.onresize = doDHTMLResize;
}

// Layer objects registry
const layerObjs: Record<string, LayerObject> = {};

/**
 * LayerObject wraps DOM DIVs and provides common positioning functions
 */
class LayerObject {
  obj: HTMLElement;
  style: CSSStyleDeclaration;
  name: string;
  x: number = 0;
  y: number = 0;
  width: number = 0;
  height: number = 0;
  z: number = 0;
  visible: boolean;
  hostObj: LayerObject | null = null;
  hostAlignX: number | null = null;
  hostAlignY: number | null = null;
  alignToHostX: number | null = null;
  alignToHostY: number | null = null;
  alignOffsetX: number = 0;
  alignOffsetY: number = 0;

  constructor(objName: string) {
    this.obj = document.getElementById(objName + "Div")!;
    this.style = this.obj.style;
    this.name = objName;
    this.initMetric();
    this.z = parseInt(this.style.zIndex) || 0;
    this.visible = this.style.visibility !== "hidden";
    if (!this.visible) {
      this.moveTo(0, 0);
    }
    layerObjs[objName] = this;
  }

  /**
   * Sets X, Y, width and height of the object
   */
  initMetric(): void {
    const pos = findPosition(this.obj);
    this.x = pos.left;
    this.y = pos.top;

    if (this.obj) {
      if (this.obj.offsetWidth) {
        this.width = this.obj.offsetWidth;
        this.height = this.obj.offsetHeight;
      } else {
        // Legacy NS4 clip support
        const clip = (this.obj as any).clip;
        if (clip) {
          this.width = clip.right;
          this.height = clip.bottom;
        }
      }
    }
  }

  /**
   * Moves a layer to position (x,y) in the document
   */
  moveTo(x: number | null, y: number | null): void {
    if (x != null) {
      this.x = x;
      this.style.left = this.x + "px";
    }
    if (y != null) {
      this.y = y;
      this.style.top = this.y + "px";
    }
  }

  /**
   * Moves a layer so that its upper-left corner coincides with another layer's
   */
  moveToObj(obj: LayerObject): void {
    this.x = obj.x;
    this.y = obj.y;
    this.style.left = this.x + "px";
    this.style.top = this.y + "px";
  }

  /**
   * Moves a layer relative to its current position
   */
  moveRel(x: number, y: number): void {
    this.x += x;
    this.y += y;
    this.style.left = this.x + "px";
    this.style.top = this.y + "px";
  }

  /**
   * Moves this layer on the X-axis relative to a target layer
   * @param target - Target layer to align to (null to use cached values)
   * @param myAlign - Alignment of this layer (_LEFT, _CENTER, _RIGHT)
   * @param targetAlign - Alignment of target layer
   * @param offset - Additional pixel offset
   */
  moveRelToX(
    target: LayerObject | null,
    myAlign?: number,
    targetAlign?: number,
    offset?: number
  ): void {
    let t: LayerObject;
    let mA: number;
    let tA: number;
    let o: number;

    if (target != null) {
      this.hostObj = t = target;
      this.hostAlignX = tA = targetAlign!;
      this.alignToHostX = mA = myAlign!;
      this.alignOffsetX = o = offset!;
    } else {
      t = this.hostObj!;
      mA = this.alignToHostX!;
      tA = this.hostAlignX!;
      o = this.alignOffsetX;
    }

    let finalOffset = 0;
    if (mA === _CENTER) {
      finalOffset = -Math.floor(this.width / 2);
    } else if (mA === _RIGHT) {
      finalOffset = -this.width;
    }
    if (tA === _CENTER) {
      finalOffset += Math.floor(t.width / 2);
    } else if (tA === _RIGHT) {
      finalOffset += t.width;
    }
    finalOffset += o;
    this.moveTo(t.x + finalOffset, null);
  }

  /**
   * Moves this layer on the Y-axis relative to a target layer
   * @param target - Target layer to align to (null to use cached values)
   * @param myAlign - Alignment of this layer (_TOP, _CENTER, _BOTTOM)
   * @param targetAlign - Alignment of target layer
   * @param offset - Additional pixel offset
   */
  moveRelToY(
    target: LayerObject | null,
    myAlign?: number,
    targetAlign?: number,
    offset?: number
  ): void {
    let t: LayerObject;
    let mA: number;
    let tA: number;
    let o: number;

    if (target != null) {
      this.hostObj = t = target;
      this.hostAlignY = tA = targetAlign!;
      this.alignToHostY = mA = myAlign!;
      this.alignOffsetY = o = offset!;
    } else {
      t = this.hostObj!;
      mA = this.alignToHostY!;
      tA = this.hostAlignY!;
      o = this.alignOffsetY;
    }

    let finalOffset = 0;
    if (mA === _CENTER) {
      finalOffset = -Math.floor(this.height / 2);
    } else if (mA === _BOTTOM) {
      finalOffset = -this.height;
    }
    if (tA === _CENTER) {
      finalOffset += Math.floor(t.height / 2);
    } else if (tA === _BOTTOM) {
      finalOffset += t.height;
    }
    finalOffset += o;
    this.moveTo(null, t.y + finalOffset);
  }

  /**
   * Sets the clipping dimensions for this layer
   */
  setClip(t: number, r: number, b: number, l: number): void {
    this.style.clip = "rect(" + t + "px " + r + "px " + b + "px " + l + "px)";
  }

  /**
   * Sets the z-Index (stacking) value for this layer
   */
  setZ(zval: number): void {
    this.z = zval;
    this.style.zIndex = String(this.z);
  }

  /**
   * Displays this layer
   */
  show(): void {
    this.visible = true;
    this.style.visibility = "visible";
  }

  /**
   * Hides this layer
   */
  hide(): void {
    this.visible = false;
    this.style.visibility = "hidden";
  }

  /**
   * Sets visibility based on boolean value
   */
  setVisible(v: boolean): void {
    this.visible = v;
    this.style.visibility = this.visible ? "visible" : "hidden";
  }

  /**
   * Rewrites the contents of this layer
   */
  rewrite(t: string): void {
    const el = document.getElementById(this.name + "Div");
    if (el) {
      el.innerHTML = t;
    }
  }

  /**
   * Opens a URL and copies its contents into this layer
   */
  openURL(url: string): void {
    let u = url;
    const iframe = (globalThis as any)["_" + this.name + "Frame"];

    if (u.indexOf("?") > 0) {
      u += "&";
    } else {
      u += "?";
    }
    u += "_width=" + this.width;
    u +=
      "&_onload=parent.layerObjs['" +
      this.name +
      "'].rewrite(parent._" +
      this.name +
      "Frame.document.body.innerHTML)";
    if (iframe && iframe.document) {
      iframe.document.location.href = u;
    }
  }
}

///////////////////
// EVENTREGISTRY //
///////////////////

const eventRegistryObjs: Record<string, EventRegistry> = {};

type EventCallback = (() => void) | string;

/**
 * EventRegistry manages custom events for named objects
 */
class EventRegistry {
  events: Record<string, EventCallback[]> = {};

  /**
   * Register an event handler callback
   * @param eventName - Name of the event (e.g., 'click', 'mouseover')
   * @param callback - Function to execute when event fires
   */
  registerEvent(eventName: string, callback: () => void): void {
    let event = this.events[eventName];
    if (event == null) {
      this.events[eventName] = event = [];
    }
    event.push(callback);
  }

  /**
   * Execute all handlers for an event
   */
  executeEvent(eventName: string): void {
    const event = this.events[eventName];
    if (event == null) return;
    for (const callback of event) {
      if (typeof callback === "function") {
        callback();
      } else {
        throw new Error("EventRegistry.registerEvent no longer accepts strings. Pass a callback function instead.");
      }
    }
  }
}

/**
 * Register an event handler for a named object
 * @param eventName - Name of the event
 * @param objName - Name of the object to register the event on
 * @param callback - Function to execute when event fires
 */
function registerEvent(eventName: string, objName: string, callback: () => void): void {
  getEventRegistry(objName, true)!.registerEvent(eventName, callback);
}

/**
 * Execute an event for a named object
 */
function doEvent(eventName: string, objName: string): void {
  const eventRegistry = getEventRegistry(objName);
  if (eventRegistry != null) {
    eventRegistry.executeEvent(eventName);
  }
}

/**
 * Get or create an event registry for a named object
 */
function getEventRegistry(name: string, create?: boolean): EventRegistry | null {
  let eventRegistry = eventRegistryObjs[name];
  if (create === true && eventRegistry == null) {
    eventRegistryObjs[name] = new EventRegistry();
    eventRegistry = eventRegistryObjs[name];
  }
  return eventRegistry || null;
}

// Export for module usage
export {
  _DHTMLTOOLS_JS_,
  _TOP,
  _LEFT,
  _CENTER,
  _MIDDLE,
  _BOTTOM,
  _RIGHT,
  _whereAmI,
  resizeEvents,
  doDHTMLResize,
  registerResizeEvent,
  layerObjs,
  LayerObject,
  eventRegistryObjs,
  EventRegistry,
  registerEvent,
  doEvent,
  getEventRegistry
};

// Expose on globalThis for backwards compatibility
(globalThis as any)._DHTMLTOOLS_JS_ = _DHTMLTOOLS_JS_;
(globalThis as any)._TOP = _TOP;
(globalThis as any)._LEFT = _LEFT;
(globalThis as any)._CENTER = _CENTER;
(globalThis as any)._MIDDLE = _MIDDLE;
(globalThis as any)._BOTTOM = _BOTTOM;
(globalThis as any)._RIGHT = _RIGHT;
(globalThis as any)._whereAmI = _whereAmI;
(globalThis as any).resizeEvents = resizeEvents;
(globalThis as any).doDHTMLResize = doDHTMLResize;
(globalThis as any).registerResizeEvent = registerResizeEvent;
(globalThis as any).layerObjs = layerObjs;
(globalThis as any).LayerObject = LayerObject;
(globalThis as any).eventRegistryObjs = eventRegistryObjs;
(globalThis as any).EventRegistry = EventRegistry;
(globalThis as any).registerEvent = registerEvent;
(globalThis as any).doEvent = doEvent;
(globalThis as any).getEventRegistry = getEventRegistry;

// Script resource notification
if (typeof _SR_ !== "undefined" && _SR_ !== null) {
  _SR_.notify("dhtmltools.js");
}
